import { formatDate } from '@/lib/date-time';
/**
 * @file components/dispatch/confirmYearlyDuplicateCases.tsx
 * @description 新建案件前的「本年度重複派案」排查提示（2026-09-15 回報序22）。
 *
 * 呼叫 GET /api/cases/duplicate-check 取得該客戶本年度既有案件；有資料時彈窗列出
 * （同驗證服務類型者標紅），由派案人員決定「仍要建立」或「取消」。排查請求失敗時
 * 僅記錄錯誤並放行，避免排查功能故障阻斷派案。
 */

import { Space, Tag, Typography } from 'antd';
import { apiClient } from '@/lib/api-client';
import { message, modal } from '@/lib/antd-app';

/** 後端回傳的本年度既有案件 */
interface YearlyDuplicateCase {
  id: number;
  caseCode: string;
  verificationType: string;
  workflowState: string;
  createdAt: string;
  dispatchedAt: string | null;
  teamLeadName: string | null;
  sameVerificationType: boolean;
}

/**
 * 排查並確認是否繼續建立案件。
 *
 * @param clientId          客戶 ID
 * @param verificationTypes 本次欲建立的驗證服務類型
 * @param clientLabel       客戶顯示名稱（彈窗標題用）
 * @returns true＝繼續建立；false＝使用者取消
 */
export async function confirmYearlyDuplicateCases(
  clientId: number,
  verificationTypes: readonly string[],
  clientLabel?: string,
): Promise<boolean> {
  let items: YearlyDuplicateCase[] = [];
  try {
    const params = new URLSearchParams({ clientId: String(clientId) });
    for (const t of verificationTypes) params.append('verificationType', t);
    const res = await apiClient.get<{ items: YearlyDuplicateCase[] }>(
      `/api/cases/duplicate-check?${params.toString()}`,
    );
    items = res.items ?? [];
  } catch (err) {
    // 排查失敗不阻斷建案，僅提示
    console.error('[dispatch] yearly duplicate case check failed', err);
    message.warning('本年度重複案件排查失敗，請自行確認是否已建立過本案件');
    return true;
  }
  if (!items.length) return true;

  const sameTypeCount = items.filter((i) => i.sameVerificationType).length;
  return new Promise<boolean>((resolve) => {
    modal.confirm({
      title: sameTypeCount
        ? `${clientLabel ?? '此客戶'}本年度已有相同驗證服務類型的案件`
        : `${clientLabel ?? '此客戶'}本年度已有 ${items.length} 件案件`,
      width: 600,
      content: (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            請確認是否重複建案／重複派案。確定仍要建立新案件嗎？
          </Typography.Text>
          {items.map((it) => (
            <div key={it.id} style={{ fontSize: 13 }}>
              <Space size={6} wrap>
                <Typography.Text strong>{it.caseCode}</Typography.Text>
                <Tag color={it.sameVerificationType ? 'red' : 'default'}>{it.verificationType}</Tag>
                <Tag color="blue">{it.workflowState}</Tag>
                <Typography.Text type="secondary">
                  建立 {formatDate(it.createdAt)}
                  {it.dispatchedAt
                    ? `・派案 ${formatDate(it.dispatchedAt)}`
                    : '・尚未派案'}
                  {it.teamLeadName ? `・稽核組長 ${it.teamLeadName}` : ''}
                </Typography.Text>
              </Space>
            </div>
          ))}
        </Space>
      ),
      okText: '仍要建立',
      okButtonProps: { danger: sameTypeCount > 0 },
      cancelText: '取消',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}
